import React from 'react';

// Base icon size
const BASE_SIZE = 40;

interface IconProps {
  size?: number;
  color?: string;
  label?: string;
}

// Main Node - Star shape
export const MainNodeIcon: React.FC<IconProps> = ({ size = BASE_SIZE, color = '#FFD700', label }) => (
  <svg width={size} height={size} viewBox="0 0 40 40">
    <polygon
      points="20,2 25,15 38,15 27,24 31,38 20,29 9,38 13,24 2,15 15,15"
      fill={color}
      stroke="#000"
      strokeWidth="1"
    />
    {label && (
      <text x="20" y="22" textAnchor="middle" fill="#000" fontSize="10" fontWeight="bold">
        {label}
      </text>
    )}
  </svg>
);

// Regular Node - Circle
export const NodeIcon: React.FC<IconProps> = ({ size = BASE_SIZE, color = '#4CAF50', label }) => (
  <svg width={size} height={size} viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="16" fill={color} stroke="#000" strokeWidth="2" />
    {label && (
      <text x="20" y="24" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold">
        {label}
      </text>
    )}
  </svg>
);

// Internet Node - Router/WiFi symbol
export const InternetNodeIcon: React.FC<IconProps> = ({ size = BASE_SIZE, color = '#2196F3', label }) => (
  <svg width={size} height={size} viewBox="0 0 40 40">
    <rect x="8" y="24" width="24" height="10" rx="2" fill={color} stroke="#000" strokeWidth="1" />
    <path
      d="M20 22 Q10 18 10 12 M20 22 Q30 18 30 12 M20 18 Q14 15 14 11 M20 18 Q26 15 26 11 M20 14 Q17 12 17 9"
      fill="none"
      stroke={color}
      strokeWidth="2"
    />
    <circle cx="20" cy="8" r="3" fill={color} />
    {label && (
      <text x="20" y="32" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">
        {label}
      </text>
    )}
  </svg>
);

// Generic Device - Square
export const DeviceIcon: React.FC<IconProps> = ({ size = BASE_SIZE, color = '#9C27B0', label }) => (
  <svg width={size} height={size} viewBox="0 0 40 40">
    <rect x="6" y="6" width="28" height="28" rx="4" fill={color} stroke="#000" strokeWidth="2" />
    {label && (
      <text x="20" y="24" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">
        {label}
      </text>
    )}
  </svg>
);

// Bomb Device - Circle with timer
export const BombIcon: React.FC<IconProps> = ({ size = BASE_SIZE, color = '#F44336', label }) => (
  <svg width={size} height={size} viewBox="0 0 40 40">
    <circle cx="20" cy="22" r="14" fill={color} stroke="#000" strokeWidth="2" />
    <rect x="17" y="4" width="6" height="6" rx="1" fill="#333" />
    <line x1="20" y1="10" x2="20" y2="14" stroke="#333" strokeWidth="2" />
    <text x="20" y="26" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">
      BOMB
    </text>
    {label && (
      <text x="20" y="34" textAnchor="middle" fill="#fff" fontSize="8">
        {label}
      </text>
    )}
  </svg>
);

// Flag Device
export const FlagIcon: React.FC<IconProps> = ({ size = BASE_SIZE, color = '#FF9800', label }) => (
  <svg width={size} height={size} viewBox="0 0 40 40">
    <rect x="8" y="6" width="3" height="32" fill="#8B4513" />
    <polygon points="11,6 36,13 11,20" fill={color} stroke="#000" strokeWidth="1" />
    {label && (
      <text x="23" y="16" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">
        {label}
      </text>
    )}
  </svg>
);

// Respawn Point - Cross/Plus
export const RespawnIcon: React.FC<IconProps> = ({ size = BASE_SIZE, color = '#4CAF50', label }) => (
  <svg width={size} height={size} viewBox="0 0 40 40">
    <rect x="16" y="4" width="8" height="32" rx="2" fill={color} stroke="#000" strokeWidth="1" />
    <rect x="4" y="16" width="32" height="8" rx="2" fill={color} stroke="#000" strokeWidth="1" />
    {label && (
      <text x="20" y="24" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">
        {label}
      </text>
    )}
  </svg>
);

// Terminal/Computer Device
export const TerminalIcon: React.FC<IconProps> = ({ size = BASE_SIZE, color = '#607D8B', label }) => (
  <svg width={size} height={size} viewBox="0 0 40 40">
    <rect x="6" y="8" width="28" height="20" rx="2" fill={color} stroke="#000" strokeWidth="2" />
    <rect x="8" y="10" width="24" height="14" fill="#1a1a2e" />
    <rect x="14" y="28" width="12" height="4" fill={color} />
    <rect x="10" y="32" width="20" height="3" rx="1" fill={color} />
    <text x="20" y="21" textAnchor="middle" fill="#0f0" fontSize="8">
      &gt;_
    </text>
    {label && (
      <text x="20" y="38" textAnchor="middle" fill="#fff" fontSize="7">
        {label}
      </text>
    )}
  </svg>
);

// Objective/Target
export const ObjectiveIcon: React.FC<IconProps> = ({ size = BASE_SIZE, color = '#E91E63', label }) => (
  <svg width={size} height={size} viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="16" fill="none" stroke={color} strokeWidth="2" />
    <circle cx="20" cy="20" r="11" fill="none" stroke={color} strokeWidth="2" />
    <circle cx="20" cy="20" r="6" fill={color} />
    {label && (
      <text x="20" y="24" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold">
        {label}
      </text>
    )}
  </svg>
);

// Map of device types to icons
export const getDeviceIcon = (deviceType: string): React.FC<IconProps> => {
  const iconMap: Record<string, React.FC<IconProps>> = {
    'MapMainNode': MainNodeIcon,
    'MainNode': MainNodeIcon,
    'MapNode': NodeIcon,
    'Node': NodeIcon,
    'InternetNode': InternetNodeIcon,
    'Bomb': BombIcon,
    'Flag': FlagIcon,
    'Respawn': RespawnIcon,
    'Terminal': TerminalIcon,
    'Objective': ObjectiveIcon,
  };

  return iconMap[deviceType] || DeviceIcon;
};

// Get color based on device type
export const getDeviceColor = (deviceType: string): string => {
  const colorMap: Record<string, string> = {
    'MapMainNode': '#FFD700',
    'MainNode': '#FFD700',
    'MapNode': '#4CAF50',
    'Node': '#4CAF50',
    'InternetNode': '#2196F3',
    'Bomb': '#F44336',
    'Flag': '#FF9800',
    'Respawn': '#4CAF50',
    'Terminal': '#607D8B',
    'Objective': '#E91E63',
  };

  return colorMap[deviceType] || '#9C27B0';
};

export default {
  MainNodeIcon,
  NodeIcon,
  InternetNodeIcon,
  DeviceIcon,
  BombIcon,
  FlagIcon,
  RespawnIcon,
  TerminalIcon,
  ObjectiveIcon,
  getDeviceIcon,
  getDeviceColor,
};
